import React, { useCallback, useId, useMemo, useState } from "react";
import { Dropdown } from "semantic-ui-react";
import {
  useAssignSubmissionAssigneesMutation,
  useGetAccountUsersQuery,
  useUnassignSubmissionAssigneesMutation,
} from "src/services";
import { CNCSubmission, InventorySubmission } from "src/types/Submission";
import { toastSuccess, toastError } from "src/utils/toast";
import { User } from "../User/types";

interface AssignedToDropdownProps {
  submission?: InventorySubmission | CNCSubmission;
  primacyAgencyAccountId?: number;
}

const AssignedToDropdown: React.FC<AssignedToDropdownProps> = ({
  submission,
  primacyAgencyAccountId,
}) => {
  const searchInputId = useId().replace(/:/g, '');
  const [isLoadingAssignee, setIsLoadingAssignee] = useState(false);

  const { data: accountWriteUsers } = useGetAccountUsersQuery({
    accountId: primacyAgencyAccountId ?? 0,
    permissions: ["stateportal:write"],
    excludePermissions: ["platform:admin"]
  });

  const [assignSubmissionUser] = useAssignSubmissionAssigneesMutation();
  const [unassignUsers] = useUnassignSubmissionAssigneesMutation();

  const assigneeOptions = useMemo(() => {
    return accountWriteUsers?.items
      ?.map((reviewer: User) => ({
        value: reviewer.id.toString(),
        text: `${reviewer.firstName} ${reviewer.lastName}`,
      }))
      .sort((a, b) => a.text.localeCompare(b.text))
      .concat({
        value: "null",
        text: "Unassigned",
      });
  }, [accountWriteUsers]);

  const assigneeText = useMemo(() => {
    const selectedUser: User | undefined = accountWriteUsers?.items?.find(
      (reviewer: User) => reviewer.id === submission?.assigneeUserId
    );
    const dropdownText = selectedUser
      ? `${selectedUser.firstName} ${selectedUser.lastName}`
      : "Unassigned";
    return dropdownText;
  }, [accountWriteUsers, submission?.assigneeUserId]);

  const handleAssignReviewer = useCallback(
    async (submissionId: number, userId: number | null): Promise<void> => {
      try {
        if (userId === null) {
          await unassignUsers([submissionId]).unwrap();
          toastSuccess("Reviewer unassigned successfully");
          return;
        }
        await assignSubmissionUser({
          assignUsersDtos: [
            {
              submissionId,
              userId,
            },
          ],
        }).unwrap();
        toastSuccess("Reviewer assigned successfully");
      } catch (error) {
        toastError("Failed to assign reviewer");
      }
    },
    [assignSubmissionUser, unassignUsers]
  );

  return (
    <div>
      <Dropdown
        search
        searchInput={{ id: `assign-reviewer-dropdown-${searchInputId}`, 'aria-label': 'Assign reviewer' }}
        onChange={(event, { value }) => {
        if (event.type !== "click") return;
        (async () => {
          setIsLoadingAssignee(true);
          if (value === "null" && submission) {
            await handleAssignReviewer(submission.id, null);
          } else if (!isNaN(parseInt(value as string)) && submission) {
            await handleAssignReviewer(
              submission.id,
              parseInt(value as string)
            );
          } else {
            toastError("Invalid value");
          }
          setIsLoadingAssignee(false);
        })();
      }}
      options={assigneeOptions}
      value={submission?.assigneeUserId ?? ""}
      text={assigneeText}
      disabled={isLoadingAssignee}
      scrolling
      style={{ display: "flex", alignItems: "center" }}
    />
    </div>
  );
};

export default AssignedToDropdown;
